
import { useState } from 'react'
import { Icon } from '@/components/ui/Icon'
import { PLN_DAYA_TIERS, POWER_CONSTANTS } from '@/lib/power'
import type { PowerPlanSettings } from '@/lib/power-plan'
import { card, cx, inputSm, label as labelCls } from '@/lib/ui'

/** Sets people actually hire, in kVA. */
const GENSET_SIZES = [5, 10, 15, 20, 30, 40, 60, 80, 100, 125, 150, 200, 250]

/**
 * What the plan is checked against.
 *
 * Four controls are visible because four decisions change the answer. The rest
 * sit behind a disclosure — but every one of them, opened or not, is printed
 * verbatim on the export. An assumption that only exists in a closed panel is
 * an assumption nobody agreed to.
 */
export function SupplyStrip({
  settings,
  onChange,
}: {
  settings: PowerPlanSettings
  onChange: (next: PowerPlanSettings) => void
}) {
  const [open, setOpen] = useState(false)
  const set = <K extends keyof PowerPlanSettings>(key: K, value: PowerPlanSettings[K]) =>
    onChange({ ...settings, [key]: value })

  return (
    <section className={cx(card, 'px-5 py-4')} aria-label="Supply">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <Field label="Supply">
          <span className="text-sm tabular-nums">
            {settings.voltage} V · {settings.frequency} Hz
          </span>
          <span className="ml-1.5 text-[11px] text-muted">single phase</span>
        </Field>

        <Field label="Source">
          <div className="flex items-center gap-1.5">
            <select
              aria-label="Supply source"
              className={inputSm}
              value={settings.supply.kind}
              onChange={(event) => {
                const kind = event.target.value as PowerPlanSettings['supply']['kind']
                if (kind === 'PLN') set('supply', { kind: 'PLN', tierVa: 7700 })
                else if (kind === 'GENSET') set('supply', { kind: 'GENSET', kva: 20, rating: 'PRIME' })
                else set('supply', { kind: 'NONE' })
              }}
            >
              <option value="NONE">Not set</option>
              <option value="PLN">PLN service</option>
              <option value="GENSET">Generator</option>
            </select>

            {settings.supply.kind === 'PLN' && (
              <select
                aria-label="PLN daya tier"
                className={inputSm}
                value={settings.supply.tierVa}
                onChange={(event) => set('supply', { kind: 'PLN', tierVa: Number(event.target.value) })}
              >
                {PLN_DAYA_TIERS.map((tier) => (
                  <option key={tier.va} value={tier.va}>
                    {tier.va} VA · {tier.amps} A
                  </option>
                ))}
              </select>
            )}

            {settings.supply.kind === 'GENSET' && (
              <>
                <select
                  aria-label="Generator size"
                  className={inputSm}
                  value={settings.supply.kva}
                  onChange={(event) =>
                    set('supply', {
                      kind: 'GENSET',
                      kva: Number(event.target.value),
                      rating: settings.supply.kind === 'GENSET' ? settings.supply.rating : 'PRIME',
                    })
                  }
                >
                  {GENSET_SIZES.map((kva) => (
                    <option key={kva} value={kva}>
                      {kva} kVA
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Generator rating"
                  className={inputSm}
                  value={settings.supply.rating ?? 'PRIME'}
                  onChange={(event) =>
                    set('supply', {
                      kind: 'GENSET',
                      kva: settings.supply.kind === 'GENSET' ? settings.supply.kva : 20,
                      rating: event.target.value as 'PRIME' | 'STANDBY',
                    })
                  }
                >
                  <option value="PRIME">Prime</option>
                  <option value="STANDBY">Standby</option>
                </select>
              </>
            )}
          </div>
        </Field>

        <Field label="Headroom">
          <div className="flex items-center gap-1.5">
            <input
              aria-label="Headroom percent"
              type="number"
              min={0}
              max={100}
              step={5}
              value={settings.headroomPct}
              onChange={(event) => set('headroomPct', clamp(Number(event.target.value), 0, 100))}
              className={cx(inputSm, 'w-16 tabular-nums')}
            />
            <span className="text-[11px] text-muted">%</span>
          </div>
        </Field>

        <Field label="Breaker policy">
          <select
            aria-label="Breaker policy"
            className={inputSm}
            value={settings.usablePct}
            onChange={(event) => set('usablePct', Number(event.target.value))}
          >
            <option value={100}>IEC 60364 / PUIL — no derate</option>
            <option value={80}>NEC 80% continuous</option>
          </select>
        </Field>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted transition-colors hover:text-foreground"
        >
          <Icon name={open ? 'chevronDown' : 'chevronRight'} className="h-3 w-3" />
          Advanced supply
        </button>
      </div>

      {settings.usablePct === 80 && (
        <p className="mt-2.5 text-[11px] text-muted">
          The 80% continuous-load rule is an NEC concept. Indonesia follows PUIL 2011 (SNI
          0225:2011), which is IEC-derived and has no equivalent rule — leave this at IEC unless you
          are deliberately sizing to a US standard.
        </p>
      )}

      {open && (
        <div className="mt-4 grid gap-4 border-t border-[var(--border)] pt-4 sm:grid-cols-3">
          <Field label="Show length">
            <div className="flex items-center gap-1.5">
              <input
                aria-label="Show hours"
                type="number"
                min={1}
                max={48}
                value={settings.showHours}
                onChange={(event) => set('showHours', clamp(Number(event.target.value), 1, 48))}
                className={cx(inputSm, 'w-16 tabular-nums')}
              />
              <span className="text-[11px] text-muted">hours — battery and charger budget</span>
            </div>
          </Field>

          <Field label="LED content fraction">
            <div className="flex items-center gap-1.5">
              <input
                aria-label="Content fraction"
                type="number"
                min={0.1}
                max={1}
                step={0.05}
                value={settings.contentFraction}
                onChange={(event) => set('contentFraction', clamp(Number(event.target.value), 0.1, 1))}
                className={cx(inputSm, 'w-20 tabular-nums')}
              />
              <span className="text-[11px] text-muted">of full white</span>
            </div>
          </Field>

          <Field label="Voltage basis">
            <label className="flex items-center gap-2 text-[11px]">
              <input
                type="checkbox"
                checked={settings.worstCaseVoltage}
                onChange={(event) => set('worstCaseVoltage', event.target.checked)}
                className="h-3.5 w-3.5 rounded border-[var(--border-strong)]"
              />
              Report current at {POWER_CONSTANTS.worstCaseVoltage} V (−10% nominal)
            </label>
          </Field>

          <div className="sm:col-span-3">
            <p className={labelCls}>Fixed in this version</p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-muted">
              External DC adapter efficiency {POWER_CONSTANTS.adapterEff}. PoE: PD → PSE port ÷{' '}
              {POWER_CONSTANTS.poeCableEff}, PSE port → AC × {POWER_CONSTANTS.poePseOverhead}. UPS
              conversion {POWER_CONSTANTS.upstreamEff}. Generator kVA → kW at{' '}
              {POWER_CONSTANTS.gensetPf}. PLN tiers converted at {POWER_CONSTANTS.plnTierVoltage} V,
              which is PLN&rsquo;s own basis, while the plan works at {settings.voltage} V. All of
              these print on the export whether or not this panel was opened.
            </p>
          </div>
        </div>
      )}
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className={labelCls}>{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  )
}

const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min
